import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import * as mammoth from 'mammoth';
import DOMPurify from 'dompurify';
import * as XLSX from 'xlsx';
import { FiX, FiExternalLink, FiDownload, FiMaximize2, FiMinimize2 } from 'react-icons/fi';
import {
  type AttachmentPreviewKind,
  getAttachmentPreviewKind,
  googleDocsViewerEmbedUrl,
  officeEmbedViewerUrl,
  officeFileSupportsLocalPreview,
  resolveAttachmentLabel,
} from '@/utils/attachmentPreview';

interface AttachmentPreviewModalProps {
  isOpen: boolean;
  onClose: () => void;
  fileUrl: string;
  fileName: string;
}

const SHEET_MAX_ROWS = 500;

function TextFilePreview({ url, title }: { url: string; title: string }) {
  const [content, setContent] = useState<string | null>(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(url, { mode: 'cors', credentials: 'omit' });
        if (!res.ok) throw new Error('fetch failed');
        const text = await res.text();
        if (!cancelled) setContent(text);
      } catch {
        if (!cancelled) setFailed(true);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [url]);

  if (failed) {
    return (
      <div className="flex flex-col flex-1 min-h-0">
        <p className="text-xs text-amber-800 bg-amber-50 border-b border-amber-200 px-3 py-2 shrink-0">
          Loaded via browser iframe instead (direct fetch was blocked).
        </p>
        <iframe title={title} src={url} className="w-full flex-1 min-h-[65vh] border-0 bg-white" />
      </div>
    );
  }

  if (content === null) {
    return (
      <div className="flex items-center justify-center min-h-[40vh] text-sm text-gray-500">Loading preview…</div>
    );
  }

  return (
    <pre className="w-full flex-1 overflow-auto p-4 text-[13px] leading-relaxed text-gray-800 bg-white font-mono whitespace-pre-wrap break-words min-h-[50vh] max-h-[calc(90vh-56px)]">
      {content}
    </pre>
  );
}

function SheetGridPreview({ rows }: { rows: (string | number | null | undefined)[][] }) {
  if (!rows.length) {
    return <p className="p-6 text-sm text-gray-500">This sheet is empty.</p>;
  }
  const display = rows.slice(0, SHEET_MAX_ROWS);
  const header = display[0] ?? [];
  const body = display.slice(1);
  const truncated = rows.length > SHEET_MAX_ROWS;

  return (
    <div className="flex flex-col flex-1 min-h-0">
      {truncated && (
        <p className="text-xs text-amber-900 bg-amber-50 border-b border-amber-200 px-3 py-2 shrink-0">
          Showing first {SHEET_MAX_ROWS} rows of {rows.length}. Download the file for the full workbook.
        </p>
      )}
      <div className="overflow-auto flex-1 min-h-[50vh] max-h-[calc(90vh-100px)] bg-white">
        <table className="min-w-full text-[12px] border-collapse border border-gray-200">
          <thead className="sticky top-0 z-10">
            <tr>
              {header.map((cell, i) => (
                <th
                  key={i}
                  className="border border-gray-200 bg-gray-100 px-2 py-1.5 text-left font-semibold text-gray-800 whitespace-nowrap"
                >
                  {cell === null || cell === undefined ? '' : String(cell)}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {body.map((row, ri) => (
              <tr key={ri} className="hover:bg-gray-50/80">
                {header.map((_, ci) => (
                  <td key={ci} className="border border-gray-200 px-2 py-1 text-gray-800 align-top">
                    {row[ci] === null || row[ci] === undefined ? '' : String(row[ci])}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

/**
 * In-app preview: images, PDFs, video, and audio inline; Word (.docx) and Excel (.xls/.xlsx) parsed locally;
 * other Office types via Microsoft/Google embed when the file URL is publicly reachable.
 */
export default function AttachmentPreviewModal({ isOpen, onClose, fileUrl, fileName }: AttachmentPreviewModalProps) {
  const displayName = resolveAttachmentLabel(fileName, fileUrl);
  const kind: AttachmentPreviewKind = getAttachmentPreviewKind(fileName, fileUrl);
  const localOffice = officeFileSupportsLocalPreview(fileName, fileUrl);
  const [imageError, setImageError] = useState(false);
  const [officeEngine, setOfficeEngine] = useState<'microsoft' | 'google'>('microsoft');
  const [officeTab, setOfficeTab] = useState<'inApp' | 'online'>('inApp');
  const [sheetRows, setSheetRows] = useState<(string | number | null | undefined)[][] | null>(null);
  const [docxHtml, setDocxHtml] = useState<string | null>(null);
  const [localOfficeError, setLocalOfficeError] = useState<string | null>(null);
  const [localOfficeLoading, setLocalOfficeLoading] = useState(false);
  const [isMaximized, setIsMaximized] = useState(false);

  useEffect(() => {
    if (isOpen) {
      setImageError(false);
      setOfficeEngine('microsoft');
      setOfficeTab('inApp');
      setSheetRows(null);
      setDocxHtml(null);
      setLocalOfficeError(null);
      setLocalOfficeLoading(false);
      setIsMaximized(false);
    }
  }, [isOpen, fileUrl]);

  useEffect(() => {
    if (!isOpen || kind !== 'office' || !localOffice) return;

    let cancelled = false;
    setLocalOfficeLoading(true);
    setLocalOfficeError(null);
    setSheetRows(null);
    setDocxHtml(null);

    (async () => {
      try {
        const res = await fetch(fileUrl, { mode: 'cors', credentials: 'omit' });
        if (!res.ok) throw new Error(`Could not fetch file (${res.status})`);
        const ab = await res.arrayBuffer();
        if (cancelled) return;

        if (localOffice === 'sheet') {
          const wb = XLSX.read(ab, { type: 'array' });
          const first = wb.SheetNames[0];
          if (!first) throw new Error('No sheets in workbook');
          const sheet = wb.Sheets[first];
          const json = XLSX.utils.sheet_to_json(sheet, { header: 1, defval: '' }) as (string | number)[][];
          if (cancelled) return;
          setSheetRows(json.length ? json : [[]]);
        } else if (localOffice === 'docx') {
          const result = await mammoth.convertToHtml({ arrayBuffer: ab });
          if (cancelled) return;
          setDocxHtml(DOMPurify.sanitize(result.value || '<p>(No content)</p>'));
        }
      } catch (e: unknown) {
        if (!cancelled) {
          setLocalOfficeError(e instanceof Error ? e.message : 'Preview failed');
          setOfficeTab('online');
        }
      } finally {
        if (!cancelled) setLocalOfficeLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [isOpen, kind, localOffice, fileUrl]);

  useEffect(() => {
    if (!isOpen) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  const officeSrc = officeEngine === 'microsoft' ? officeEmbedViewerUrl(fileUrl) : googleDocsViewerEmbedUrl(fileUrl);
  const showOfficeTabs = kind === 'office' && localOffice;

  const shellMax = isMaximized ? 'p-0 items-stretch' : 'p-4 items-center justify-center';
  const panelMax = isMaximized
    ? 'max-w-none w-full h-full max-h-[100dvh] rounded-none'
    : 'max-w-5xl w-full max-h-[90vh] rounded-lg';

  return createPortal(
    <div
      className={`fixed inset-0 z-[240] flex bg-black/60 ${shellMax}`}
      role="dialog"
      aria-modal="true"
      aria-label={`Preview: ${displayName}`}
      onClick={onClose}
    >
      <div
        className={`bg-white shadow-2xl flex flex-col overflow-hidden ${panelMax}`}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between gap-3 px-4 py-3 border-b border-gray-200 shrink-0">
          <h2 className="text-sm font-semibold text-gray-900 truncate min-w-0" title={displayName}>
            {displayName}
          </h2>
          <div className="flex items-center gap-2 shrink-0 flex-wrap justify-end">
            <button
              type="button"
              onClick={() => setIsMaximized((v) => !v)}
              className="inline-flex items-center gap-1 px-2 py-1 text-xs text-gray-700 hover:bg-gray-100 rounded font-medium border border-gray-200"
              aria-label={isMaximized ? 'Exit full screen preview' : 'Maximize preview'}
            >
              {isMaximized ? <FiMinimize2 className="w-3.5 h-3.5" /> : <FiMaximize2 className="w-3.5 h-3.5" />}
              {isMaximized ? 'Restore' : 'Maximize'}
            </button>
            <a
              href={fileUrl}
              download={displayName}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1 px-2 py-1 text-xs text-gray-700 hover:bg-gray-100 rounded font-medium border border-gray-200"
            >
              <FiDownload className="w-3.5 h-3.5" />
              Download
            </a>
            <a
              href={fileUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1 px-2 py-1 text-xs text-[#0078d4] hover:bg-blue-50 rounded font-medium"
            >
              <FiExternalLink className="w-3.5 h-3.5" />
              Open in new tab
            </a>
            <button
              type="button"
              onClick={onClose}
              className="p-1.5 rounded hover:bg-gray-100 text-gray-500"
              aria-label="Close preview"
            >
              <FiX className="w-5 h-5" />
            </button>
          </div>
        </div>

        <div className="flex-1 min-h-0 overflow-hidden bg-gray-50 flex flex-col">
          {kind === 'image' && !imageError && (
            <div className="flex items-center justify-center p-4 overflow-auto min-h-[50vh] max-h-[calc(90vh-56px)]">
              <img
                src={fileUrl}
                alt={displayName}
                className="max-w-full max-h-[75vh] object-contain"
                onError={() => setImageError(true)}
              />
            </div>
          )}

          {kind === 'image' && imageError && (
            <div className="flex flex-col items-center justify-center gap-3 p-8 min-h-[50vh] text-center">
              <p className="text-sm text-gray-600">Could not display this image in the preview.</p>
              <a
                href={fileUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1 text-sm text-[#0078d4] font-medium hover:underline"
              >
                <FiExternalLink className="w-4 h-4" />
                Open in new tab
              </a>
            </div>
          )}

          {kind === 'pdf' && (
            <iframe
              title={displayName}
              src={fileUrl}
              className={`w-full flex-1 border-0 bg-white ${isMaximized ? 'min-h-0 h-full' : 'min-h-[70vh]'}`}
            />
          )}

          {kind === 'video' && (
            <div className={`flex flex-1 min-h-0 items-center justify-center bg-black ${isMaximized ? 'p-2' : 'p-4'}`}>
              <video
                src={fileUrl}
                controls
                playsInline
                className="max-w-full max-h-full w-full object-contain"
                title={displayName}
              />
            </div>
          )}

          {kind === 'audio' && (
            <div className="flex flex-1 min-h-[40vh] items-center justify-center p-8 bg-white">
              <audio src={fileUrl} controls className="w-full max-w-md" title={displayName} />
            </div>
          )}

          {kind === 'office' && (
            <div className="flex flex-col flex-1 min-h-0">
              {showOfficeTabs && (
                <div className="flex items-center gap-1 px-3 py-2 bg-gray-100 border-b border-gray-200 shrink-0 text-[11px]">
                  <span className="text-gray-600 mr-1">Preview:</span>
                  <button
                    type="button"
                    onClick={() => setOfficeTab('inApp')}
                    disabled={!localOffice}
                    className={`px-2 py-0.5 rounded font-medium ${
                      officeTab === 'inApp'
                        ? 'bg-white text-[#0078d4] shadow-sm'
                        : 'text-gray-600 hover:bg-gray-200 disabled:opacity-40'
                    }`}
                  >
                    {localOffice === 'docx' ? 'Word (in app)' : localOffice === 'sheet' ? 'Excel (in app)' : 'In app'}
                  </button>
                  <button
                    type="button"
                    onClick={() => setOfficeTab('online')}
                    className={`px-2 py-0.5 rounded font-medium ${
                      officeTab === 'online'
                        ? 'bg-white text-[#0078d4] shadow-sm'
                        : 'text-gray-600 hover:bg-gray-200'
                    }`}
                  >
                    Online viewer
                  </button>
                </div>
              )}

              {officeTab === 'inApp' && localOffice === 'sheet' && (
                <>
                  {localOfficeLoading && (
                    <div className="flex items-center justify-center min-h-[40vh] text-sm text-gray-500">
                      Loading spreadsheet…
                    </div>
                  )}
                  {localOfficeError && !localOfficeLoading && (
                    <p className="text-xs text-red-700 bg-red-50 px-3 py-2 border-b border-red-100">{localOfficeError}</p>
                  )}
                  {sheetRows && !localOfficeLoading && <SheetGridPreview rows={sheetRows} />}
                </>
              )}

              {officeTab === 'inApp' && localOffice === 'docx' && (
                <>
                  {localOfficeLoading && (
                    <div className="flex items-center justify-center min-h-[40vh] text-sm text-gray-500">
                      Loading document…
                    </div>
                  )}
                  {localOfficeError && !localOfficeLoading && (
                    <p className="text-xs text-red-700 bg-red-50 px-3 py-2 border-b border-red-100">{localOfficeError}</p>
                  )}
                  {docxHtml && !localOfficeLoading && (
                    <div
                      className="overflow-auto flex-1 min-h-[50vh] max-h-[calc(90vh-120px)] bg-white px-6 py-4 text-[13px] text-gray-900 leading-relaxed prose prose-sm max-w-none
                        [&_p]:mb-2 [&_ul]:list-disc [&_ul]:pl-6 [&_ol]:list-decimal [&_ol]:pl-6 [&_table]:border-collapse [&_td]:border [&_td]:border-gray-200 [&_td]:px-2 [&_td]:py-1 [&_th]:border [&_th]:border-gray-200 [&_th]:px-2 [&_th]:py-1"
                      dangerouslySetInnerHTML={{ __html: docxHtml }}
                    />
                  )}
                </>
              )}

              {officeTab === 'inApp' && !localOffice && (
                <p className="text-xs text-gray-600 px-3 py-2 bg-amber-50 border-b border-amber-100">
                  PowerPoint and older .doc files need the online viewer (or open in a new tab).
                </p>
              )}

              {(officeTab === 'online' || !localOffice) && (
                <div className="flex flex-col flex-1 min-h-0">
                  <div className="flex items-center gap-2 px-3 py-2 bg-gray-100 border-b border-gray-200 shrink-0 text-[11px]">
                    <span className="text-gray-600 mr-1">Viewer:</span>
                    <button
                      type="button"
                      onClick={() => setOfficeEngine('microsoft')}
                      className={`px-2 py-0.5 rounded font-medium ${
                        officeEngine === 'microsoft' ? 'bg-white text-[#0078d4] shadow-sm' : 'text-gray-600 hover:bg-gray-200'
                      }`}
                    >
                      Microsoft
                    </button>
                    <button
                      type="button"
                      onClick={() => setOfficeEngine('google')}
                      className={`px-2 py-0.5 rounded font-medium ${
                        officeEngine === 'google' ? 'bg-white text-[#0078d4] shadow-sm' : 'text-gray-600 hover:bg-gray-200'
                      }`}
                    >
                      Google
                    </button>
                  </div>
                  <iframe title={displayName} src={officeSrc} className="w-full flex-1 min-h-[60vh] border-0 bg-white" />
                  <p className="text-[11px] text-gray-500 px-3 py-2 bg-gray-100 border-t border-gray-200 shrink-0 leading-snug">
                    Online viewers need a URL reachable from the internet (for example Azure Blob with SAS). If the frame
                    is blank, try the other provider or <strong>Open in new tab</strong>.
                  </p>
                </div>
              )}
            </div>
          )}

          {kind === 'text' && <TextFilePreview url={fileUrl} title={displayName} />}

          {kind === 'none' && (
            <div className="flex flex-col items-center justify-center gap-3 p-8 min-h-[50vh] text-center">
              <p className="text-sm text-gray-600">
                No built-in preview for this type. Open the file in a new tab or download it.
              </p>
              <a
                href={fileUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1 text-sm text-[#0078d4] font-medium hover:underline"
              >
                <FiExternalLink className="w-4 h-4" />
                Open in new tab
              </a>
            </div>
          )}
        </div>
      </div>
    </div>,
    document.body,
  );
}
